import { Instance } from "@gadgetinc/mobx-quick-tree";
import type { AppSettings } from "../AppSettings";
import type { ShopifyConnectionConfig } from "../connections/ShopifyConnectionConfig";
import type { DataModel } from "../database/DataModel";

export const SummonSetupMessage = "Ok, let's build it! I'll spin up all of the hosting and infrastructure for your app.";

export const SummonCopy = {
  Branding:
    "While I build your app, let's make a few quick styling decisions that I can apply to the UI components we'll use to build your frontend. You can change all of this later, so don't spend too much time on it.",
  StartBackendSetup:
    "Excellent. I've got a good idea of how to break your problem down.\n\nTo start, I'm going to set up your database one table at a time.",
  AuthSetup: "Let's start with the tables that power your application's login system.",
  BuildShopifyModels:
    "Let's start by adding all of the data we're going to need from Shopify. To avoid dealing with Shopify's rate limits, we'll sync data from Shopify to replica tables we'll setup in our own database.",
  BuildCoreModels: (setting: AppSettings) =>
    `Now I'll add ${"the rest of"} the tables that will power the business logic of your application.`,
  BuildFrontendRoutes: "Let's build your first frontend routes; we can take care of the rest later together.",
  ModelsIntro: `Alright, the data models are ready.`,
  FeaturedModelIntro: (model: DataModel) =>
    `Models like \`${model.apiIdentifier}\` are more than just empty Postgres tables.\n\nLet's take a look.`,
  CoreModelAction: (settings: AppSettings) =>
    `Calling the API will trigger the corresponding ${
      settings.canGenerateTypeScriptFiles ? "TypeScript" : "JavaScript"
    } action file to run.`,
  CoreModelActionFinalMessage:
    "The file can do anything we want it to do. By default, it's setup to take the incoming data and save it to the database.",
  CoreModelActionApiTrigger: "Each model has autogenerated CRUD API endpoints you can call from anywhere.",
  LoadFeaturedModelCode: (modelApiIdentifier: string, actionApiIdentifier: string) =>
    `Opening the \`${modelApiIdentifier}/${actionApiIdentifier}\` action`,
  ShopifyModelWithFirstPartyModelsIntro: "Shopify models are setup using the same trigger-action framework as well. Let's take a look.",
  ShopifyModelsSourceOfTruth:
    "They are, however, triggered by events from Shopify as the source of truth.\n\nTo catch those events, I setup webhook handlers and wired them up to your CRUD actions for these tables.",
  ShopifyModelAction: `Each table comes with CRUD actions I’ve setup to keep your tables in sync with Shopify.`,
  ShopifyModelActionApiTrigger:
    "To catch those events, I've setup webhook handlers and wired them up to your CRUD actions for these tables.",
  ShopifyModelActionFinalMessage:
    "The file can do anything we want it to do. By default, I’ve set it up to take the incoming data from Shopify and save it to the database.",
  ConnectToShopify: (_connectionConfig: Instance<typeof ShopifyConnectionConfig>) =>
    `Alright, we setup the database, including the Shopify tables, let's connect these to Shopify so we can work on the frontend.\n\nI'll help you set up a Shopify connection with the following scopes:`,
  AddDataStart: `Let's call our API to run those actions and add some sample data to our tables.`,
  AddDataToFeaturedCoreModelStart: (model: DataModel) => `Here's the \`${model.apiIdentifier}\` table with some sample data.`,
  AddDataToFeaturedCoreModelDone: `Now that we've added some data, the data editor lets you view, search, and edit each row in the table.`,
  FrontendSetup: "Alright, let's go to the frontend.",
  FrontendPreviewUnauthenticated: (settings: AppSettings) =>
    settings.userModelKey
      ? `Here's the logged out area of your app.\n\nYour app has secure email/password and Google SSO authentication setup.`
      : `Here's the frontend of your app.`,
  FrontendPreviewAuthenticated:
    "Let's log in to your application using a user record that I've created for you. Once logged in, this is the first page that your users will see.",
  GenerateShopifySampleData: "We've successfully connected to Shopify, let's add some sample data to our database tables.",
  SummonDone: "Ok, tell me what you'd like to do next!",
  SummonError:
    "I'm sorry, I had an error while setting up your app. It's been reported to the Gadget team. Let's try again! Where do you want to start?",
  Setup: {
    hosting: "Bootstrapping hosting on Google Cloud",
    devUrl: "Setting up a development environment at",
    prodUrl: "Setting up a production environment at",
    database: "Starting up a Postgres database",
    backend: "Generating a Node.js backend",
    frontend: "Generating a React frontend on Vite",
    dependencies: "Installing dependencies",
    auth: "Setting up authentication",
    models: "Building models",
    sampleData: "Generating sample data",
    frontendRoutes: "Building your initial frontend routes",
    todoList: "Planning build process",
  },
};

export const SummonAppLoadingMessage = "Hold tight your app is loading...";
